/**
 * @hostesa/feature-flags — Flag catalog
 *
 * Defines the typed union of all known feature flags and the Zod schema that
 * validates a flags record supplied from the backend / config source.
 *
 * IMPORTANT: Feature flags control UI visibility and route access.
 * They are NOT an authorization layer.
 * A route is accessible only when session + RBAC + ABAC + flag ALL pass.
 * Flags never substitute for permission checks in auth-client.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Canonical flag identifiers
// ---------------------------------------------------------------------------

/**
 * Union of all supported feature flag names (§14 of the master prompt).
 *
 * When adding a new flag:
 *  1. Add the literal to this union.
 *  2. Add a default entry in DEFAULT_FLAGS below.
 *  3. Document the rollout intent in a comment here.
 */
export type FeatureFlag =
  /** In-stay operations board: service requests, exceptions, room status. MVP Phase 2. */
  | 'in_stay_operations'
  /** LiveKit browser voice handoff: allows hotel staff to join an active call via audio. */
  | 'livekit_handoff'
  /** Backoffice provider diagnostics tab: shows Vapi/LiveKit raw provider metadata.
   *  Backoffice-only, never surfaced in hotel-facing routes. */
  | 'backoffice_provider_diagnostics'
  /** Knowledge onboarding wizard: website crawl → draft review → activation flow. */
  | 'knowledge_onboarding'
  /** Script Studio: script version authoring, approval, and handoff rule configuration. */
  | 'script_studio'
  /** Integrations management: PMS connect, capability test, enable/disable. */
  | 'integrations_management'
  /** Usage and cost reporting dashboard. */
  | 'usage_reporting';

// ---------------------------------------------------------------------------
// Flag record type
// ---------------------------------------------------------------------------

/** A complete resolved flags map — every flag must have a boolean value. */
export type FlagsRecord = Record<FeatureFlag, boolean>;

// ---------------------------------------------------------------------------
// Default values — conservative / rollout-safe
// ---------------------------------------------------------------------------

/**
 * Default flag values applied when no backend/config override is provided.
 *
 * Policy: new flags default to `false` (opt-in) until the owning team
 * enables them for target environments or hotels. This ensures that an
 * incomplete rollout does not accidentally expose unfinished surfaces.
 *
 * Exception: features that are stable and intended for all users from day one
 * may default to `true` once they pass production readiness review.
 */
export const DEFAULT_FLAGS: FlagsRecord = {
  // Phase 2 in-stay operations: off by default (not MVP Phase 1)
  in_stay_operations: false,
  // LiveKit handoff requires a backend-issued room token; off until stable
  livekit_handoff: false,
  // Provider diagnostics: operator-only; off by default for safety
  backoffice_provider_diagnostics: false,
  // Knowledge onboarding is part of core onboarding flow — on by default
  knowledge_onboarding: true,
  // Script Studio: core feature — on by default
  script_studio: true,
  // Integrations management: core feature — on by default
  integrations_management: true,
  // Usage reporting: core feature — on by default
  usage_reporting: true,
};

// ---------------------------------------------------------------------------
// Zod schema for validation
// ---------------------------------------------------------------------------

/**
 * Schema for a partial flags record supplied from outside (e.g., backend API
 * or environment config). Unknown keys are stripped; missing keys fall back
 * to DEFAULT_FLAGS at evaluation time via createFlagEvaluator.
 */
export const flagsRecordSchema = z.object({
  in_stay_operations: z.boolean().optional(),
  livekit_handoff: z.boolean().optional(),
  backoffice_provider_diagnostics: z.boolean().optional(),
  knowledge_onboarding: z.boolean().optional(),
  script_studio: z.boolean().optional(),
  integrations_management: z.boolean().optional(),
  usage_reporting: z.boolean().optional(),
});

/** Partial flags record (all keys optional) — used in override shapes. */
export type PartialFlagsRecord = z.infer<typeof flagsRecordSchema>;

/**
 * Full flags record schema — all keys required.
 * Used to validate a completely resolved flags map.
 */
export const fullFlagsRecordSchema = z.object({
  in_stay_operations: z.boolean(),
  livekit_handoff: z.boolean(),
  backoffice_provider_diagnostics: z.boolean(),
  knowledge_onboarding: z.boolean(),
  script_studio: z.boolean(),
  integrations_management: z.boolean(),
  usage_reporting: z.boolean(),
});
